import type { Track } from '../domain/icy-parse';
import type { StreamVia } from '../domain/stream-url';
import { type Lang, plural, translate, translateOptional } from '../i18n';

/**
 * Oblikovanje vrednosti za vmesnik (P4 §7).
 *
 * Vse skozi `Intl` in slovar — nikoli konkatenacija. Podpičje ali vejica
 * med kodekom in bitrateom sta v angleščini in slovenščini različna, pika
 * pa loči tisočice le v enem od njiju.
 */

/** Ločilo med enakovrednimi podatki v isti vrstici. Ločilo, ne prevod. */
const SEPARATOR = ' · ';

export function formatBitrate(lang: Lang, bitrate: number | undefined): string | null {
  if (bitrate === undefined || bitrate <= 0) return null;
  return translate(lang, 'meta.bitrate', { n: bitrate });
}

export function formatRoute(lang: Lang, route: StreamVia | null): string | null {
  if (route === null) return null;
  return translate(lang, route === 'proxy' ? 'route.proxy' : 'route.direct');
}

/**
 * Ime države iz kode, v jeziku vmesnika.
 *
 * Zapisano ime bi bilo v enem od obeh jezikov vedno tuje, zato seed hrani
 * samo `countryCode` (T2 §9). `Intl.DisplayNames` pozna vse kode ISO 3166-1;
 * ob neznani vrne kodo samo, kar je še vedno boljše od praznega mesta.
 */
export function formatCountry(
  lang: Lang,
  station: { countryCode?: string; country?: string },
): string | null {
  if (station.countryCode === undefined || station.countryCode === '') {
    return station.country ?? null;
  }
  try {
    const names = new Intl.DisplayNames(lang, { type: 'region' });
    return names.of(station.countryCode) ?? station.countryCode;
  } catch {
    return station.countryCode;
  }
}

/**
 * Prvi žanr postaje, preveden, kadar ga poznamo.
 *
 * Žanri so shranjeni **v angleščini**, ker jih tako pošilja radio-browser
 * (T2 §9) — dvojezičen slovar bi pomenil, da `rock` in `rok` nista ista
 * stvar. Prevod je torej stvar izpisa, ne shrambe. Neznan žanr se izpiše
 * takšen, kot je: v fazi 4 jih bo iz kataloga prišlo na stotine in ugibanje
 * bi bilo slabše od surove oznake.
 */
export function formatGenre(lang: Lang, station: { tags: readonly string[] }): string | null {
  const first = station.tags[0];
  if (first === undefined || first === '') return null;

  return translateOptional(lang, `genre.${first}`) ?? first;
}

/**
 * Opisni del podnaslova: **žanr · država**.
 *
 * Ločen od tehničnega, ker je pisava druga. Mono je predpisan za bitrate in
 * kodek (CLAUDE.md, signal ≠ status), ne za žanr in državo — in je toliko
 * širši, da je v mono pisavi celotna vrstica na 390px prekipela in se
 * odrezala z elipso že pri kodeku.
 */
export function formatStationDescription(
  lang: Lang,
  station: { tags: readonly string[]; countryCode?: string; country?: string },
): string {
  return joinMeta([formatGenre(lang, station), formatCountry(lang, station)]);
}

/**
 * Tehnični del podnaslova: **bitrate**, v mono pisavi.
 *
 * ⚠️ **Kodeka tu namerno ni.** Vrstica ima na 390px poleg besedila še ikono
 * in dva cilja za dotik; štirje podatki se ne izidejo in DS jo odreže z
 * elipso — izgubi se zadnji, torej bitrate. Od kodeka in bitratea je bitrate
 * povednejši, kodek pa ostane viden v medijski vrstici, ko postaja igra.
 *
 * Nevtralno, brez sodbe o kakovosti: „192 kbps" ni opozorilo in „320 kbps"
 * ni uspeh (CLAUDE.md, signal ≠ status).
 */
export function formatStationTech(lang: Lang, station: { bitrate?: number }): string {
  const bitrate = formatBitrate(lang, station.bitrate);
  return bitrate === null ? '' : `${SEPARATOR}${bitrate}`;
}

/**
 * Skladba za izpis: **izvajalec — naslov**.
 *
 * Ločilo je v slovarju in ne v kodi: pomišljaj z presledki je tipografska
 * odločitev jezika, ne konkatenacija (P4 §7). Kadar izvajalca ni — govorne
 * postaje in marsikateri Icecast ga ne pošljejo — ostane sam naslov, brez
 * praznega mesta za ločilom.
 */
export function formatTrack(lang: Lang, track: Track): string {
  if (track.artist === null || track.artist === '') return track.title;
  return translate(lang, 'player.track', { artist: track.artist, title: track.title });
}

export function joinMeta(parts: (string | null)[]): string {
  return parts.filter((part): part is string => part !== null && part !== '').join(SEPARATOR);
}

/**
 * Ojačitev pasu (P2 §6.3, D2 §6).
 *
 * Dve obliki, ker sta dve rabi:
 *   · `short` je odčitek pod drsnikom — `+4`, `0`, `−2`. Nič je brez predznaka:
 *     je nevtralna sredina lestvice in ne vrednost, ki bi šla v katerokoli smer.
 *   · polna je `aria-valuetext`, torej z enoto — sama številka brez
 *     »decibelov« bralcu zaslona ne pove ničesar (D2 §6).
 *
 * ⚠️ **Slovenska dvojina** (P4 §9): 1 decibel · 2 decibela · 3 decibele ·
 * 5 decibelov. `Intl.PluralRules` dela z **absolutno** vrednostjo, zato
 * −3 dB pravilno pade v `few` — a preverjeno je s testom, ne z domnevo.
 *
 * ⚠️ **Minus se med jezikoma razlikuje** (izmerjeno 2026-07-21): angleščina
 * piše navadni vezaj `-2`, slovenščina pravi minus U+2212 `−2`. Natanko zato
 * gre številka skozi `Intl` in ne skozi konkatenacijo (P4 §7).
 */
export function formatDecibels(lang: Lang, db: number, opts?: { short?: boolean }): string {
  // `exceptZero`, ne `always`: nič nima predznaka. „+0 dB" bi bilo videti kot
  // druga vrsta vrednosti, čeprav je nevtralna sredina lestvice.
  const signed = new Intl.NumberFormat(lang, { signDisplay: 'exceptZero' }).format(db);
  if (opts?.short === true) return signed;
  // Prek `plural()`, ne prek ročne kategorije: tako tip zahteva **vse štiri**
  // slovenske oblike in manjkajoča postane napaka ob prevajanju (T1 §11).
  return plural(lang, 'settings.eq.db', db, { value: signed });
}

/**
 * Frekvenca pasu: `31` / `1k` na oznaki, `31 Hz` / `1 kHz` za bralnik.
 *
 * Kratka oblika je brez enote namerno — pod desetimi drsniki na 360px je
 * »16000 Hz« desetkrat toliko besedila, kolikor ga vrstica prenese, pomen pa
 * nosi `aria-label`, ki je poln.
 */
export function formatHertz(lang: Lang, hz: number, opts?: { short?: boolean }): string {
  const numbers = new Intl.NumberFormat(lang);
  const kilo = hz >= 1000;
  const value = kilo ? hz / 1000 : hz;
  if (opts?.short === true) return kilo ? `${numbers.format(value)}k` : numbers.format(value);
  return translate(lang, kilo ? 'settings.eq.khz' : 'settings.eq.hz', { n: value });
}

/**
 * Odstotek glasnosti (P2 §3.5).
 *
 * Prek `Intl` in ne s konkatenacijo `%` (P4 §7): presledek pred znakom je v
 * slovenščini nedeljiv in obvezen (`50 %`), v angleščini ga ni (`50%`).
 * Vrednost je delež 0–1, kot jo hrani `Settings.volume` — `style: 'percent'`
 * jo pomnoži sam, zato tu ni ročnega množenja s sto.
 */
export function formatPercent(lang: Lang, ratio: number): string {
  return new Intl.NumberFormat(lang, {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(ratio);
}

/** Polnoč tistega dne, po **krajevni** uri. */
function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * Žig vnosa v Zgodovini: **ura danes, sicer še dan** (P2 §4.13).
 *
 * ⚠️ **Sama ura pove premalo.** Zgodovina je združena po postaji in ne po
 * času, `VIEW_LIMIT` pa je štetje vnosov in ne dni — dva vnosa iste postaje
 * ob 14:32 sta bila zato nerazločljiva, čeprav ju je ločil teden.
 *
 * Meja dneva je **krajevna polnoč in ne 24 ur**: vnos ob 23:50 je »včeraj«
 * tudi ob 00:10, ne šele čez cel dan. Razlika gre skozi `Math.round` nad
 * polnočema, da je poletni čas (dan s 23 ali 25 urami) ne premakne.
 *
 * Letnica pride šele ob prehodu leta — »24. dec.« brez nje je v januarju
 * dvoumno. Datum sestavi `Intl`, ločilo med njim in uro pa slovar: angleščina
 * piše »Jul 24, 14:32«, slovenščina »24. jul. 14:32«, kjer bi bila vejica za
 * piko okrajšave odveč (P4 §7).
 */
export function formatHistoryStamp(lang: Lang, at: number, now: number): string {
  const stamp = new Date(at);
  let time: string;
  try {
    time = new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit' }).format(stamp);
  } catch {
    return '';
  }

  const days = Math.round((startOfDay(new Date(now)) - startOfDay(stamp)) / 86_400_000);
  // Žig iz prihodnosti (premaknjena ura, uvožena kopija) je »danes«: ugibati,
  // koliko dni naprej, bi bilo videti kot napaka aplikacije.
  if (days <= 0) return time;
  if (days === 1) return translate(lang, 'history.at.yesterday', { time });

  const date = new Intl.DateTimeFormat(lang, {
    day: 'numeric',
    month: 'short',
    ...(stamp.getFullYear() === new Date(now).getFullYear() ? {} : { year: 'numeric' }),
  }).format(stamp);
  return translate(lang, 'history.at.dated', { date, time });
}

/**
 * Ura kot `H:MM:SS` ali `MM:SS` (P2 §4.5, §5.3).
 *
 * Ni `Intl`: dvopičje ni ločilo tisočic in ga ne loči jezik — je zapis ure,
 * enak povsod. Cifre so v mono `tabular-nums` (razred `.tnum` na klicnem
 * mestu), da se odštevanje ne trese. Ure so brez vodilne ničle, minute in
 * sekunde vedno dvomestne, da je stolpec poravnan.
 */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const seconds = total % 60;
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3600);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}
